import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Search, Menu, X, Instagram, Facebook, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../store/useStore';
export function Header() {
  const {
    cart,
    toggleCart
  } = useStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  const isActive = (path: string) => location.pathname === path;
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Shop',
    path: '/shop'
  }, {
    name: 'Our Story',
    path: '/our-story'
  }, {
    name: 'Collabs',
    path: '/colabs'
  }, {
    name: 'Contact Us',
    path: '/contact'
  }];
  return <header className="w-full bg-white sticky top-0 z-50 shadow-sm transition-all duration-300" data-id="element-189">
      {/* Announcement Bar */}
      <motion.div initial={false} animate={{
      height: scrolled ? 0 : 'auto',
      opacity: scrolled ? 0 : 1,
      overflow: 'hidden'
    }} transition={{
      duration: 0.3,
      ease: 'easeInOut'
    }} className="bg-black text-white px-4 flex flex-col md:flex-row justify-between items-center text-[10px] font-medium tracking-[0.2em] uppercase border-b border-gray-800" data-id="element-190">
        <div className="py-2 md:py-3 flex-1 text-center md:text-left px-2 text-[9px] md:text-[10px] tracking-widest md:tracking-[0.2em]" data-id="element-191">
          Adorned in African Culture | Shop New Arrivals | Gift Cards Available
        </div>
        <div className="py-1 md:py-3 flex items-center space-x-4 md:space-x-6" data-id="element-192">
          <div className="flex items-center space-x-3" data-id="element-193">
            <a href="https://www.instagram.com/trilynllc/" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors" data-id="element-194">
              <Instagram size={14} data-id="element-195" />
            </a>
            <a href="https://www.facebook.com/TriLynLLC" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors" data-id="element-196">
              <Facebook size={14} data-id="element-197" />
            </a>
          </div>
          <Link to="/product/gift-card" className="hidden sm:block" data-id="element-198">
            <button className="bg-gold text-black px-4 py-1.5 text-[10px] font-bold hover:bg-white transition-colors duration-300" data-id="element-199">
              Buy Gift Card
            </button>
          </Link>
        </div>
      </motion.div>

      {/* Main Bar */}
      <div className="container mx-auto px-4 py-2 md:py-4 flex justify-between items-center" data-id="element-200">
        {/* Logo - Left */}
        <div className="flex-1 flex justify-start" data-id="element-201">
          <Link to="/" data-id="element-202">
            <img src="https://cdn.magicpatterns.com/uploads/pKM35Hxacor8pvdYX3c3hd/pasted-image.png" alt="TriLyn Logo" className="h-10 md:h-16 object-contain" data-id="element-203" />
          </Link>
        </div>

        {/* Navigation - Center (Hidden on small, visible on large) */}
        <nav className="hidden lg:block flex-[2]" data-id="element-204">
          <ul className="flex justify-center space-x-8 text-[10px] font-bold uppercase tracking-[0.2em]" data-id="element-205">
            {navLinks.map(link => <li key={link.path} data-id="element-206">
                <Link to={link.path} className={`transition-colors hover:text-gold ${isActive(link.path) ? 'text-gold' : 'text-black'}`} data-id="element-207">
                  {link.name}
                </Link>
              </li>)}
          </ul>
        </nav>

        {/* Icons & Hamburger - Right */}
        <div className="flex-1 flex justify-end items-center space-x-3 md:space-x-6" data-id="element-208">
          <Link to="/shop" className="hover:text-gold transition-colors" data-id="element-209">
            <Search size={18} md:size={20} data-id="element-210" />
          </Link>
          <button onClick={() => toggleCart(true)} className="relative hover:text-gold transition-colors" data-id="element-211">
            <ShoppingBag size={18} md:size={20} data-id="element-212" />
            <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center" data-id="element-213">
              {cartCount}
            </span>
          </button>

          {/* Fun Hamburger Button */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="z-[60] relative w-10 h-10 flex items-center justify-center text-gold transition-colors lg:hidden" data-id="element-214">
            <motion.div animate={isMenuOpen ? 'open' : 'closed'} className="flex flex-col justify-center items-center gap-1.5" data-id="element-215">
              <motion.span variants={{
              closed: {
                rotate: 0,
                y: 0
              },
              open: {
                rotate: 45,
                y: 6
              }
            }} className="w-6 h-0.5 bg-current block rounded-full" data-id="element-216" />
              <motion.span variants={{
              closed: {
                opacity: 1,
                x: 0
              },
              open: {
                opacity: 0,
                x: 20
              }
            }} className="w-6 h-0.5 bg-current block rounded-full" data-id="element-217" />
              <motion.span variants={{
              closed: {
                rotate: 0,
                y: 0
              },
              open: {
                rotate: -45,
                y: -6
              }
            }} className="w-6 h-0.5 bg-current block rounded-full" data-id="element-218" />
            </motion.div>
          </button>
        </div>
      </div>

      {/* Fun & Unique Menu Reveal */}
      <AnimatePresence data-id="element-219">
        {isMenuOpen && <motion.div initial={{
        clipPath: 'circle(0% at 95% 5%)'
      }} animate={{
        clipPath: 'circle(150% at 95% 5%)'
      }} exit={{
        clipPath: 'circle(0% at 95% 5%)'
      }} transition={{
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1]
      }} className="fixed inset-0 bg-black z-[55] flex items-center justify-center" data-id="element-220">
            <div className="container mx-auto px-6 py-12 lg:py-20 flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12" data-id="element-221">
              {/* Left Side: Large Text/Image */}
              <motion.div initial={{
            opacity: 0,
            x: -50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} className="hidden lg:block flex-1" data-id="element-222">
                <h2 className="text-6xl lg:text-8xl font-serif text-white/10 leading-none select-none" data-id="element-223">
                  ADORNED
                  <br data-id="element-224" />
                  IN
                  <br data-id="element-225" />
                  CULTURE
                </h2>
                <div className="mt-8 border-l-2 border-gold pl-6" data-id="element-226">
                  <p className="text-gold text-sm uppercase tracking-[0.3em] font-bold" data-id="element-227">
                    TriLyn Luxury
                  </p>
                  <p className="text-white/60 text-xs mt-2 max-w-xs" data-id="element-228">
                    Experience the heritage of African craftsmanship through
                    modern elegance.
                  </p>
                </div>
              </motion.div>

              {/* Right Side: Navigation Links */}
              <nav className="flex-1 w-full lg:w-auto" data-id="element-229">
                <ul className="flex flex-col items-center lg:items-start gap-6 lg:gap-8" data-id="element-230">
                  {navLinks.map((link, i) => <motion.li key={link.path} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.5 + i * 0.1,
                duration: 0.5
              }} data-id="element-231">
                      <Link to={link.path} onClick={() => setIsMenuOpen(false)} className="group flex items-center gap-4 text-4xl md:text-6xl font-serif text-white hover:text-gold transition-all duration-300" data-id="element-232">
                        <span className="text-xs font-sans font-bold text-gold opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-233">
                          0{i + 1}
                        </span>
                        <span className="relative" data-id="element-234">
                          {link.name}
                          <motion.span className="absolute -bottom-2 left-0 w-0 h-1 bg-gold rounded-full group-hover:w-full transition-all duration-500" data-id="element-235" />
                        </span>
                        <ArrowRight className="opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-gold" size={24} data-id="element-236" />
                      </Link>
                    </motion.li>)}
                </ul>
              </nav>

              {/* Social Links Bottom Mobile */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 1
          }} className="flex lg:hidden gap-6 mt-8" data-id="element-237">
                <a href="https://www.instagram.com/trilynllc/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gold transition-colors" data-id="element-238">
                  <Instagram size={24} data-id="element-239" />
                </a>
                <a href="https://www.facebook.com/TriLynLLC" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gold transition-colors" data-id="element-240">
                  <Facebook size={24} data-id="element-241" />
                </a>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}